import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

export type IconBadgeSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconBadgeVariant = 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo' | 'gray' | 'cyan' | 'orange' | 'pink';
export type IconBadgeShape = 'rounded' | 'circle' | 'square';

/**
 * IconBadge Component - Rounded/circular container with icon inside
 *
 * @example
 * <!-- Blue folder icon badge -->
 * <app-atom-icon-badge icon="folder" variant="blue" size="md"></app-atom-icon-badge>
 *
 * <!-- Green check icon badge -->
 * <app-atom-icon-badge icon="check" variant="green" size="lg"></app-atom-icon-badge>
 *
 * <!-- Red pause icon badge (circle) -->
 * <app-atom-icon-badge icon="control-pause" variant="red" size="md" shape="circle"></app-atom-icon-badge>
 */
@Component({
  selector: 'app-atom-icon-badge',
  standalone: true,
  imports: [CommonModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [class]="containerClasses()">
      <app-atom-icon 
        [name]="icon()" 
        [size]="iconSize()" 
        [color]="iconColor()">
      </app-atom-icon>
    </div>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }
  `]
})
export class IconBadgeComponent {
  /** Icon name (Tabler icon without 'ti-' prefix) */
  icon = input.required<string>();
  
  /** Color variant */
  variant = input<IconBadgeVariant>('blue');
  
  /** Size of the badge */
  size = input<IconBadgeSize>('md');
  
  /** Shape of the badge */
  shape = input<IconBadgeShape>('rounded');
  
  /** Additional CSS classes for container */
  additionalClasses = input<string>('');

  /** Size mappings for container */
  private containerSizeMap: Record<IconBadgeSize, string> = {
    'xs': 'w-6 h-6',
    'sm': 'w-8 h-8',
    'md': 'w-10 h-10',
    'lg': 'w-12 h-12',
    'xl': 'w-14 h-14'
  };

  /** Size mappings for icon */
  private iconSizeMap: Record<IconBadgeSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'> = {
    'xs': 'xs',
    'sm': 'sm',
    'md': 'md',
    'lg': 'lg',
    'xl': 'xl'
  };

  /** Variant to background color mapping */
  private bgColorMap: Record<IconBadgeVariant, string> = {
    'blue': 'bg-blue-50',
    'green': 'bg-green-50',
    'red': 'bg-red-50',
    'yellow': 'bg-yellow-50',
    'purple': 'bg-purple-50',
    'indigo': 'bg-indigo-50',
    'gray': 'bg-gray-100',
    'cyan': 'bg-cyan-50',
    'orange': 'bg-orange-50',
    'pink': 'bg-pink-50'
  };

  /** Variant to icon color mapping */
  private iconColorMap: Record<IconBadgeVariant, string> = {
    'blue': 'text-blue-600',
    'green': 'text-green-600',
    'red': 'text-red-500',
    'yellow': 'text-yellow-600',
    'purple': 'text-purple-600',
    'indigo': 'text-indigo-600',
    'gray': 'text-gray-600',
    'cyan': 'text-cyan-600',
    'orange': 'text-orange-600',
    'pink': 'text-pink-600'
  };

  /** Shape to border-radius mapping */
  private shapeMap: Record<IconBadgeShape, string> = {
    'rounded': 'rounded-lg',
    'circle': 'rounded-full',
    'square': 'rounded-none'
  };

  /** Computed container classes */
  containerClasses = computed(() => {
    const classes = [
      'flex',
      'items-center',
      'justify-center',
      'flex-shrink-0',
      this.containerSizeMap[this.size()],
      this.bgColorMap[this.variant()],
      this.shapeMap[this.shape()]
    ];
    
    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }
    
    return classes.join(' ');
  });

  /** Computed icon size */
  iconSize = computed(() => this.iconSizeMap[this.size()]);

  /** Computed icon color */
  iconColor = computed(() => this.iconColorMap[this.variant()]);
}
